import { Button, Stack, Text } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { PasswordInput, TextInput } from '@components';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useSignIn } from '@hooks/user';

const loginSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }).min(1, { message: 'Email required' }),
  password: z.string().min(1, { message: 'Password required' }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export const Login = () => {
  const navigate = useNavigate();

  const callback = () => navigate('/batches');

  const useSignInMutation = useSignIn(callback);

  const form = useFormWithNameInputProp<LoginFormValues>({
    initialValues: { email: '', password: '' },
    validate: zodResolver(loginSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = (values) => useSignInMutation.mutate(values);

  return (
    <Stack mt="xl">
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack gap="md">
          <TextInput {...form.getInputProps('email')} data-pw="login-email-input" label="Email" size="lg" />
          <PasswordInput {...form.getInputProps('password')} data-pw="login-password-input" label="Password" size="lg" />
          {useSignInMutation.isError && (
            <Text c="red" fw={700} data-pw="login-user-error">
              {useSignInMutation.error.response?.data?.message ?? 'Login failed'}
            </Text>
          )}
          <Button data-pw="login-submit-btn" type="submit" variant="filled" size="lg">
            Sign in
          </Button>
          <Text fz="xs" c="dimmed" ta="left">
            This site is protected by reCAPTCHA and the Google <br />
            <a href="https://policies.google.com/privacy">Privacy Policy</a> and <a href="https://policies.google.com/terms">Terms of Service</a> apply.
          </Text>
        </Stack>
      </form>
      <Text ta="right" mt="md">
        <Link to={'/forgot-password'}>Forgot password?</Link>
      </Text>
    </Stack>
  );
};
